import { Autocomplete, Box, Button, Link, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AcademicDegreeDTO, GopDTO } from "../../dictionaries/models";
import { UniversityDTO } from "../../report/models";
import { brand } from "../../../shared/theme/app-theme";
import {
  ScholarshipDegreeRow,
  ScholarshipSummaryDTO,
  ScholarshipSummaryFilter,
} from "../model";

type Props = {
  data?: ScholarshipSummaryDTO;
  loading: boolean;
  periodLabel: string;
  degrees: AcademicDegreeDTO[];
  universities: UniversityDTO[];
  gops: GopDTO[];
  filter: ScholarshipSummaryFilter;
  onChange: (next: ScholarshipSummaryFilter) => void;
  onReset: () => void;
};

const fieldSx = {
  flex: "1 1 220px",
  minWidth: 180,
  maxWidth: 360,
  "& .MuiInputBase-root": { height: 40 },
};

function formatCount(value: number) {
  return (value ?? 0).toLocaleString("ru-RU");
}

function formatMoney(value: number) {
  return (value ?? 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function degreeLabel(degree: AcademicDegreeDTO) {
  return degree.nameRu || "—";
}

function universityLabel(university: UniversityDTO) {
  return university.nameRu || university.shortNameRu || "—";
}

function gopLabel(gop: GopDTO) {
  return gop.nameRu ? `${gop.code} — ${gop.nameRu}` : gop.code;
}

const ScholarshipSummary: React.FC<Props> = ({
  data,
  loading,
  periodLabel,
  degrees,
  universities,
  gops,
  filter,
  onChange,
  onReset,
}) => {
  const degreeOptions = useMemo(
    () => [...degrees].sort((a, b) => (a.degreeId ?? 0) - (b.degreeId ?? 0)),
    [degrees]
  );
  const universityOptions = useMemo(
    () =>
      [...universities].sort((a, b) =>
        universityLabel(a).localeCompare(universityLabel(b), "ru")
      ),
    [universities]
  );
  const gopOptions = useMemo(
    () => [...gops].sort((a, b) => a.code.localeCompare(b.code, "ru")),
    [gops]
  );

  const selectedDegree =
    degreeOptions.find((item) => item.degreeId === filter.degreeId) ?? null;
  const selectedUniversity =
    universityOptions.find((item) => item.universityId === filter.universityId) ??
    null;
  const selectedGop = gopOptions.find((item) => item.code === filter.gop) ?? null;

  const rows = useMemo(
    () =>
      (data?.degrees ?? []).map((row) => ({
        ...row,
        id: row.degreeId ?? "none",
      })),
    [data]
  );

  const columns = useMemo<GridColDef<ScholarshipDegreeRow & { id: number | string }>[]>(
    () => [
      {
        field: "degreeName",
        headerName: "Академическая степень",
        flex: 1,
        minWidth: 280,
      },
      {
        field: "assigned",
        headerName: "Назначена стипендия",
        width: 180,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatCount(Number(params.value ?? 0)),
      },
      {
        field: "paid",
        headerName: "Оплачена",
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatCount(Number(params.value ?? 0)),
      },
      {
        field: "unpaid",
        headerName: "Не оплачено",
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatCount(Number(params.value ?? 0)),
      },
      {
        field: "scholarshipSum",
        headerName: "Сумма стипендии",
        width: 200,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatMoney(Number(params.value ?? 0)),
      },
    ],
    []
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ mr: "auto" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 600, color: brand.ink }}>
            Сводка по стипендиям
          </Typography>
          {periodLabel ? (
            <Typography sx={{ fontSize: 13, color: brand.muted }}>{periodLabel}</Typography>
          ) : null}
        </Box>
        <Link component={RouterLink} to="/report" underline="hover" sx={{ fontSize: 14 }}>
          Отчеты для выплаты государственных стипендий
        </Link>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <Autocomplete
          sx={fieldSx}
          options={degreeOptions}
          value={selectedDegree}
          onChange={(_, option) =>
            onChange({ ...filter, degreeId: option?.degreeId })
          }
          getOptionLabel={degreeLabel}
          isOptionEqualToValue={(option, value) => option.degreeId === value.degreeId}
          renderInput={(params) => (
            <TextField {...params} label="Академическая степень" size="small" />
          )}
        />
        <Autocomplete
          sx={{ ...fieldSx, flex: "2 1 280px", maxWidth: 420 }}
          options={universityOptions}
          value={selectedUniversity}
          onChange={(_, option) =>
            onChange({ ...filter, universityId: option?.universityId })
          }
          getOptionLabel={universityLabel}
          isOptionEqualToValue={(option, value) =>
            option.universityId === value.universityId
          }
          renderInput={(params) => <TextField {...params} label="ОВПО" size="small" />}
        />
        <Autocomplete
          sx={{ ...fieldSx, flex: "2 1 280px", maxWidth: 420 }}
          options={gopOptions}
          value={selectedGop}
          onChange={(_, option) => onChange({ ...filter, gop: option?.code })}
          getOptionLabel={gopLabel}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderInput={(params) => <TextField {...params} label="ГОП" size="small" />}
        />
        <Button variant="outlined" size="medium" sx={{ height: 40 }} onClick={onReset}>
          Сбросить
        </Button>
      </Box>

      {loading ? (
        <Typography sx={{ color: "text.secondary", fontSize: 14 }}>Загрузка…</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <KpiCard title="Назначена стипендия" value={formatCount(data?.assigned ?? 0)} />
            <KpiCard title="Оплачена" value={formatCount(data?.paid ?? 0)} />
            <KpiCard title="Не оплачено" value={formatCount(data?.unpaid ?? 0)} />
            <KpiCard
              title="Расчёт не загружен"
              value={formatCount(data?.calculationNotLoaded ?? 0)}
            />
            <KpiCard
              title="Сумма стипендии"
              value={formatMoney(Number(data?.scholarshipSum ?? 0))}
            />
          </Box>
          <Typography sx={{ fontSize: 13, color: brand.muted }}>
            Записи, по которым расчёт из Финцентра ещё не загружен. Не суммируются с
            «Не оплачено».
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            В разрезе академических степеней
          </Typography>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            density="compact"
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            localeText={{ noRowsLabel: "Нет записей" }}
            sx={{
              "& .MuiDataGrid-cell": { py: 0 },
              "& .MuiDataGrid-columnHeader": { py: 0 },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
            }}
          />
        </>
      )}
    </Box>
  );
};

function KpiCard({ title, value }: { title: string; value: string }) {
  return (
    <Box
      sx={{
        flex: "1 1 180px",
        border: `1px solid ${brand.line}`,
        borderRadius: 1,
        px: 2,
        py: 1.5,
        bgcolor: brand.white,
      }}
    >
      <Typography sx={{ fontSize: 13, color: brand.muted }}>{title}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 600, color: brand.ink, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default ScholarshipSummary;
